// @flow
/* eslint-disable no-inline-comments */
/* eslint-disable eqeqeq */
/* eslint-disable no-useless-escape */
import {
  getArticleURL as getArticleURLTabular,
  getArticlePath as getArticlePathTabular,
  getArticleRel as getArticleRelTabular,
  getArticleImageUrl as getArticleImageUrlTabular,
  normalizeVideo
} from '@finect/tabular-components/Articles';
import {
  getSchemaORG as getSchemaORGTabular,
  getAuthorUrl as getAuthorUrlTabular
} from '@finect/tabular-components/ArticleSchema';
import { truncate } from '@finect/front-utils/truncate';
import { isFinecter } from '../scenes/Plus/VersionNotes/utils';
import { nl2br, addNoFollowsToExternal, linkify } from './html';
import { getArticlePlainText } from './plainText';

export const getArticlePath = (article: Object) => getArticlePathTabular(article);
export const getArticleURL = (article: Object, isClient: boolean = false) => getArticleURLTabular(article, isClient);
export const getArticleRel = (article: Object) => getArticleRelTabular(article);

// Id del grupo/organización Finect (mismo valor usado en VersionNotes, OmniNav y UserProfileLayout)
export const FINECT_GROUP_ID = 'D98F19ECF61B11E8B22202000AEE01C9';

// Un artículo es "de finecter" si lo publica el propio grupo Finect (perfil de empresa)
// o si su autor es un empleado de Finect (reutiliza isFinecter, que mira organizaciones/email).
export const isFinectArticle = (article: ?Object): boolean => {
  if (!article) return false;

  const { author } = article;

  if (author && author.subtype === 'company' && author.id === FINECT_GROUP_ID) return true;

  return isFinecter(author);
};

// Clasifica al autor para la segmentación de ads (key-value authorScope en GAM):
// 'finect' (grupo Finect o empleado Finect) y 'user' (usuario normal) -> muestran ads;
// 'group' (grupo externo) y 'adviser' (asesor de un grupo externo) -> GAM los excluye.
// Las listas por alias tienen PRIORIDAD sobre la clasificación automática: noAdsAliases fuerza
// exclusión ('group') y withAdsAliases fuerza inclusión ('finect').
export const getAuthorAdScope = (author: ?Object, options: Object = {}): string => {
  if (!author) return 'user';
  const { noAdsAliases = [], withAdsAliases = [] } = options;
  if (noAdsAliases.includes(author.alias)) return 'group';
  if (withAdsAliases.includes(author.alias)) return 'finect';
  if (author.subtype === 'company') return author.id === FINECT_GROUP_ID ? 'finect' : 'group';
  if (isFinecter(author)) return 'finect';
  const isExternalAdviser = (author.organizations || []).some(
    org => (org.roles || []).includes('adviser') && org.id !== FINECT_GROUP_ID
  );
  return isExternalAdviser ? 'adviser' : 'user';
};

export const pickArticleAuthor = (article: Object): Object | null => {
  const editor = article?.revision?.user;
  const originalAuthor = article?.author;
  if (editor && typeof editor === 'object' && editor.id && editor.id !== originalAuthor?.id) {
    return editor;
  }
  return originalAuthor || null;
};

export const getArticleEditionPath = (article: Object) => {
  // TODO: Cambiar alias por id cuando se haga el cambio de PHP a React
  if (article.author.subtype === 'user') {
    return `usuario/${article.author.alias}/articulos/${article.id}/editar`;
  } else if (article.author.subtype === 'company') {
    return `grupos/${article.author.alias}/articulos/${article.id}`;
  }

  return '#';
};

export const getArticleDescription = (html: string, maxChars: number = 120) =>
  truncate(html.replace(/<[^>]*>?/g, ''), maxChars, '...');

// Se sigue exportando desde aquí: es donde siempre ha vivido para quien lo importa.
export { getArticlePlainText };

export const getArticlePreviewText = (html: string, maxChars: number = 280) =>
  truncate(getArticlePlainText(html), maxChars, '...');

// Palabras por minuto para el tiempo estimado de lectura. 200 es el valor que usa la mayoría de
// medios para texto divulgativo; con contenido financiero denso queda del lado conservador.
/**
 * Palabras por minuto con las que se estima el tiempo de lectura.
 *
 * 160 y no 200. Las 200 son la velocidad de lectura silenciosa de prosa FÁCIL en un adulto medio;
 * lo que se publica aquí lleva cifras, porcentajes, nombres de producto y fiscalidad, y eso se lee
 * más despacio porque se vuelve atrás. Con 200 el número salía sistemáticamente corto.
 */
const WORDS_PER_MINUTE = 160;

/**
 * Texto plano de un artículo, calculado una sola vez por artículo.
 *
 * El pipeline recorre el cuerpo completo con una docena de expresiones regulares, y en un feed lo
 * necesitan dos cosas a la vez (la entradilla de reserva y los minutos de lectura) en cada
 * repintado. Sin caché, cada repintado vuelve a parsear los veinte cuerpos.
 *
 * Es un WeakMap sobre el propio objeto del artículo: mientras el feed lo tenga en el store la
 * entrada vive, y cuando se limpia desaparece sola sin retener memoria.
 */
const plainTextCache: WeakMap<Object, string> = new WeakMap();

const getCachedPlainText = (article: Object): string => {
  const cached = plainTextCache.get(article);
  if (cached !== undefined) return cached;

  const text = getArticlePlainText(article.body?.text || '');
  plainTextCache.set(article, text);

  return text;
};

// Cuenta palabras sin partir la cadena: split(' ').filter() creaba dos arrays de varios miles de
// elementos por artículo y por repintado. getArticlePlainText ya deja un solo espacio entre
// palabras, así que basta con contar los saltos de espacio a palabra.
const countWords = (text: string): number => {
  let words = 0;
  let inWord = false;

  for (let index = 0; index < text.length; index++) {
    if (text.charCodeAt(index) === 32) {
      inWord = false;
    } else if (!inWord) {
      inWord = true;
      words += 1;
    }
  }

  return words;
};

/**
 * Texto de entradilla de un artículo para listados y feeds.
 *
 * Prioriza la meta description, que está escrita precisamente para ganar el clic desde una lista.
 * Solo cae al cuerpo del artículo si viene vacía: el primer párrafo está escrito para continuar
 * el titular ante quien ya ha entrado, y a menudo arranca con la firma y el cargo del autor.
 *
 * @param article Artículo del modelo de contenidos
 * @param maxChars Longitud máxima; se corta por palabra, nunca a mitad
 */
export const getArticleDek = (article: ?Object, maxChars: number = 160): string => {
  if (!article) return '';

  const metaDescription = (article.body?.metadata?.description || article.metadata?.description || '').trim();
  // Con meta description no se toca el cuerpo: es el caso normal y sale gratis.
  const source = metaDescription || getCachedPlainText(article);

  return truncate(source, maxChars, '…');
};

/**
 * Minutos estimados de lectura de un artículo. Devuelve 0 si no hay cuerpo con el que contar,
 * para que quien lo pinte pueda omitir el dato en lugar de mostrar "0 min".
 *
 * @param article Artículo del modelo de contenidos
 */
/**
 * Palabras de las preguntas frecuentes.
 *
 * Van aparte de `body.text` en el modelo, pero se pintan en la página igual que el cuerpo, así que
 * cuentan para quien lee. Sin esto, un artículo con diez preguntas se anunciaba como si no las
 * tuviera.
 *
 * @param article Artículo del modelo de contenidos
 */
const countFaqWords = (article: ?Object): number =>
  (article?.body?.faqs || []).reduce(
    (total, faq) => total + countWords(`${faq.question || ''} ${faq.answer || ''}`.replace(/<[^>]*>/g, ' ')),
    0
  );

export const getReadingMinutes = (article: ?Object): number => {
  const text = article?.body?.text;
  if (!text && !(article?.body?.faqs || []).length) return 0;

  const words = countWords(getCachedPlainText(article)) + countFaqWords(article);
  if (!words) return 0;

  // Hacia arriba, no al más cercano: nadie lee el 60 % de un minuto, y con `round` un artículo de
  // 250 palabras se anunciaba como "1 min de lectura".
  return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE));
};

/**
 * Fecha de publicación de un artículo para el feed de noticias.
 *
 * El feed pide `orderby=-revision/created`, así que la fecha que ordena, la que agrupa por día y
 * la que se muestra tienen que ser la misma o el resultado no es monótono: un artículo antiguo
 * reeditado hoy aparecería al fondo de la lista diciendo que es de hace un rato. La card clásica
 * sigue usando `revision.updated`, así que este helper solo lo consume la variante nueva.
 *
 * @param article Artículo del modelo de contenidos
 */
export const getArticlePublishedDate = (article: ?Object): string | null =>
  article?.revision?.created || article?.revision?.updated || null;

/**
 * Duración en segundos de un artículo de vídeo o podcast.
 *
 * Sale de `articleSchema.duration`, en ISO 8601 (`PT12M34S`). Ese campo lo rellena a diario el job
 * `enrich-video-articles` de backend-tasks cruzando los artículos type=video con el catálogo de
 * YouTube, y solo si el redactor no lo ha escrito a mano. Viene ya en la respuesta del LISTADO
 * (`ArticlesByIds` parsea `articles__articleSchema`), así que no hace falta pedir el detalle.
 *
 * Devuelve null cuando el artículo no está enlazado con ningún vídeo del canal, que es el caso de
 * una parte del catálogo: quien lo use tiene que aguantar la ausencia sin reservar hueco.
 *
 * @param article Artículo del modelo de contenidos
 */
export const getMediaDurationSeconds = (article: ?Object): number | null => {
  const iso = article?.articleSchema?.duration || article?.schema?.duration;
  if (!iso || typeof iso !== 'string') return null;

  // Solo el subconjunto que emite YouTube: días, horas, minutos y segundos, sin fracciones.
  const match = /^P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(iso.trim());
  if (!match) return null;

  const [, days, hours, minutes, seconds] = match;
  if (!days && !hours && !minutes && !seconds) return null;

  const total =
    Number(days || 0) * 86400 + Number(hours || 0) * 3600 + Number(minutes || 0) * 60 + Number(seconds || 0);

  return total > 0 ? total : null;
};

/**
 * Duración en formato de reloj, como la que YouTube pinta sobre la miniatura.
 *
 * Los minutos NO se rellenan con cero a la izquierda cuando no hay horas (`9:04`, no `09:04`), que
 * es la convención de todos los reproductores. Los segundos sí, siempre.
 *
 * @param seconds Duración en segundos
 */
export const formatMediaDuration = (seconds: ?number): string => {
  if (typeof seconds !== 'number' || !isFinite(seconds) || seconds <= 0) return '';

  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  const pad = value => String(value).padStart(2, '0');

  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(rest)}` : `${minutes}:${pad(rest)}`;
};

/**
 * Duración redondeada a minutos, para la firma ("11 min de vídeo").
 *
 * @param article Artículo del modelo de contenidos
 */
export const getMediaMinutes = (article: ?Object): number => {
  const seconds = getMediaDurationSeconds(article);

  return seconds ? Math.max(1, Math.round(seconds / 60)) : 0;
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

// Meses y años como duración media, no de calendario: para "hace 3 meses" da igual si por medio
// cayó febrero, y contar meses de calendario obligaría a arrastrar la fecha además del intervalo.
const MONTH_MS = 30.44 * DAY_MS;
const YEAR_MS = 365.25 * DAY_MS;

/**
 * Tiempo transcurrido en palabras: minutos, horas, días, semanas, meses y años.
 *
 * Recibe el INTERVALO ya calculado, no la fecha, y eso es deliberado: quien pinta la lista fija una
 * sola marca de tiempo al montar y la reparte. Si esta función llamara a `new Date()` —como hace el
 * `timeSince` de front-utils— cada fila leería un reloj distinto del que uso el render de servidor
 * y la hidratación no cuadraría. Por eso tampoco se usa `timeSince`, que además no tiene semanas.
 *
 * @param elapsedMs Milisegundos transcurridos desde la publicación
 */
export const getRelativeTimeAgo = (elapsedMs: number): string => {
  if (!isFinite(elapsedMs) || elapsedMs < MINUTE_MS) return 'ahora mismo';

  const say = (value, singular, plural) => `hace ${value} ${value === 1 ? singular : plural}`;

  if (elapsedMs < HOUR_MS) return `hace ${Math.floor(elapsedMs / MINUTE_MS)} min`;
  if (elapsedMs < DAY_MS) return say(Math.floor(elapsedMs / HOUR_MS), 'hora', 'horas');
  if (elapsedMs < WEEK_MS) return say(Math.floor(elapsedMs / DAY_MS), 'día', 'días');
  if (elapsedMs < MONTH_MS) return say(Math.floor(elapsedMs / WEEK_MS), 'semana', 'semanas');
  if (elapsedMs < YEAR_MS) return say(Math.floor(elapsedMs / MONTH_MS), 'mes', 'meses');

  return say(Math.floor(elapsedMs / YEAR_MS), 'año', 'años');
};

/**
 * Miniatura de un vídeo o un podcast, con la carátula del proveedor como respaldo.
 *
 * `article.image` es lo primero porque es lo que el redactor ha elegido, pero buena parte del
 * catálogo antiguo no tiene ninguna —los vídeos de 2022 salían en gris— y ahí sí hay una carátula:
 * la de YouTube, que se deduce del id del embed.
 *
 * Se usa `hqdefault.jpg` y no `maxresdefault` ni el `vi_webp/mqdefault.webp` que usa el `Video` de
 * tabular: hqdefault es el único que existe SIEMPRE. Los otros solo se generan para vídeos subidos
 * en 720p o más, así que en el catálogo viejo devuelven 404 y volveríamos al hueco gris.
 *
 * La plantilla de la URL se repite aquí a propósito: `thumbsnails` existe en tabular pero no está
 * exportado en el índice de `Videos`, y no merece una publicación del paquete por una cadena.
 *
 * @param article Artículo de tipo vídeo o podcast
 * @param width Ancho en CSS con el que se va a pintar
 */
export const getMediaThumbUrl = (article: ?Object, width?: number): string => {
  if (!article) return '';
  if (article.image) return getArticleImageUrlTabular(article, false, width);

  const video = normalizeVideo(article.body?.url || '');

  return video && video.id ? `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg` : '';
};

// Lo que tiene que quedar de titular para que valga la pena recortar. Por debajo de esto, lo que
// sobra no era un bloque de credito: era el titular.
const MIN_TITLE_LENGTH = 20;

// Separador opcional entre el titular y el bloque de credito. La raya va suelta dentro de la clase
// pero el patron exige espacios alrededor, porque hay titulares con guiones dentro de una palabra
// («e-commerce») y con rangos («2024-2025»).
const CREDIT_SEPARATOR = '(?:\\s+[|\\-–—·:,]\\s*|\\s*[|]\\s*|\\s+)';

// Con que puede seguir la mencion de la serie sin dejar de ser credito. Medido sobre los 200
// podcasts mas recientes: de las 144 menciones con cola, 140 empiezan por «con» —«Finect Alpha con
// Jonás González, Loreto Benito y Victoria Torre»—. Lo que empieza por otra cosa («Finect Talks
// Summit 2026», «Finect Alpha 2x34 …») NO es credito, es titular, y no se toca.
const CREDIT_TAIL = '(?:\\s+(?:con|junto\\s+a|with)\\b[\\s\\S]*)?';

const escapeForRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Quita del titular el bloque de credito que la tarjeta ya pinta debajo.
 *
 * De los 200 podcasts mas recientes, 160 llevan la serie dentro del titular y la firma la repite: el
 * titular medio mide 99 caracteres, de los que solo 54 son titular. Recortado a tres lineas, el
 * lector no ve ni una cosa ni la otra. Quitando el credito quedan esos 54 y se lee entero.
 *
 * Tres condiciones, y las tres importan:
 *
 * 1. La mencion tiene que ser EXACTAMENTE la firma que se esta pintando. Si el titular dijera
 *    «| Finect Focus con Candriam» y la firma fuera otra, recortar borraria un dato que no esta en
 *    ningun otro sitio. Por eso la funcion pide el nombre de la firma en vez de buscar un patron de
 *    serie: lo que autoriza a quitarlo es que ya este en pantalla.
 * 2. La firma tiene que tener al menos dos palabras. Con una sola —«Finect»— coincidiria dentro de
 *    «Finect Talks» y se llevaria por delante el «Talks», que la firma no dice.
 * 3. La mencion tiene que cerrar el titular, sola o seguida de «con …». Ver `CREDIT_TAIL`.
 *
 * Es una regla de PRESENTACION, no un cambio del dato: el `title` del enlace sigue llevando el
 * titular completo y en la ficha del articulo no se toca nada, asi que los invitados se leen ahi.
 *
 * @param title Titular tal cual viene del modelo
 * @param bylineName Nombre que la tarjeta pinta como firma
 */
export const stripBylineFromTitle = (title: ?string, bylineName: ?string): string => {
  if (!title) return '';

  const clean = title.trim();
  const byline = (bylineName || '').trim();

  if (byline.split(/\s+/).length < 2) return clean;

  const pattern = escapeForRegExp(byline).replace(/\s+/g, '\\s+');

  // El cierre es una anticipacion negativa y no `\b`: hay firmas que acaban en signo —«A. Pérez
  // (S.A.)»— y ahi `\b` no encuentra frontera de palabra y no casaba nunca. Con esto lo que se exige
  // es que detras no siga una letra, que es lo que de verdad distingue «Finect Talks» de
  // «Finect Talkshow». La clase incluye las acentuadas, que `\w` deja fuera.
  const credit = new RegExp(`${CREDIT_SEPARATOR}${pattern}(?![\\wÀ-ÿ])${CREDIT_TAIL}$`, 'i');
  const found = clean.match(credit);

  if (!found || found.index === undefined || found.index <= 0) return clean;

  const head = clean.slice(0, found.index).replace(/[\s|·–—:,-]+$/, '');

  return head.length >= MIN_TITLE_LENGTH ? head : clean;
};

/**
 * Ruta del perfil público de un autor.
 *
 * Se calcula aquí y no se usa el `link` del modelo porque `models.author` monta SIEMPRE
 * `/usuario/<alias>` (utils/models.js), y para un grupo eso es un 404: los grupos viven en
 * `/grupos/<alias>`. Con los autores de artículos se nota poco —la mayoría son personas— pero en
 * vídeo y podcast casi todo lo firma un grupo: Finect Talks, Finect Alpha, las gestoras.
 *
 * Devuelve null si el perfil no es público, y entonces el nombre se pinta sin enlace. La comprobación
 * tiene dos formas porque el listado devuelve `features` como ARRAY en las personas
 * (`['profile']`) y como OBJETO en los grupos (`{ blog: true, … }`): llamar a `.includes` sin mirar
 * revienta con un grupo.
 *
 * @param author Autor tal cual llega del listado
 */
export const getAuthorProfilePath = (author: ?Object): string | null => {
  if (!author || !author.alias) return null;

  const isGroup = author.subtype === 'company';
  const hasProfile = Array.isArray(author.features) ? author.features.includes('profile') : isGroup;

  if (!hasProfile) return null;

  // El listado ya trae la ruta hecha en `web`, y es la que usa la cabecera del artículo. Medido
  // sobre los 20 autores distintos de los últimos 500 vídeos y 319 podcasts: los 20 la traen, y
  // coincide con la que se construye aquí (`/grupos/finect-alpha`). Se prefiere el dato de la API
  // por si algún día un perfil deja de vivir donde dice el patrón.
  return author.web || `/${isGroup ? 'grupos' : 'usuario'}/${author.alias}`;
};

// Para artículos de finecters, enriquecemos el nodo `author` con su `@id` canónico
// (= URL del perfil) y su imagen, de modo que comparta entidad con el `ProfilePage`
// de su página de perfil. `schemaId`/`schemaImage` los lee tabular en buildAuthorNode.
// Para el resto de autores el schema queda igual que antes (solo name + url).
export const getSchemaORG = (article: Object, metaDescription: string): string => {
  const author = article && article.author;
  if (author && isFinecter(author)) {
    const profileUrl = getAuthorUrlTabular(article);
    const enrichedAuthor: Object = { ...author };
    if (profileUrl) enrichedAuthor.schemaId = profileUrl;
    if (author.icon) enrichedAuthor.schemaImage = `https://www.finect.com/v4/resources/media/images/${author.icon}`;
    return getSchemaORGTabular({ ...article, author: enrichedAuthor }, metaDescription);
  }
  return getSchemaORGTabular(article, metaDescription);
};

export const getCommentHTML = (comment: string) => {
  const MENTIONED_USERS_REGEX = /(?!<a[^>]*?>)(@[a-zA-Z0-9_\-\.]{3,19}[a-zA-Z0-9_\-])(?![^<]*?<\/a>)/g;

  let mentionedUsers = comment.match(MENTIONED_USERS_REGEX);
  mentionedUsers = mentionedUsers ? mentionedUsers.filter((elem, index, self) => index == self.indexOf(elem)) : [];

  let newComment = comment;

  for (let i = 0; i < mentionedUsers.length; i++) {
    const userName = mentionedUsers[i].substr(1, mentionedUsers[i].length);

    const link = `<a href="https://www.finect.com/usuario/${userName}">@${userName}</a>`;
    newComment = newComment.replace(new RegExp(`@${userName}`, 'g'), link);
  }

  return addNoFollowsToExternal(linkify(nl2br(newComment)));
};

export const getTitleShare = (title: string) => title.replace('|', '-');
